import * as Yup from 'yup';
import {
  ShippingRestrictionScope,
  DeliveryZonesScope,
  ProductRestrictionType,
  WeightRestrictionType,
  ValueRestrictionType,
} from '@tiendanube/common/src/enums';
import { conditionalArrayRequired } from './commonValidations';

const MIN_DISTANCE_KM = 10;
const MAX_DISTANCE_KM = 10000;

const selectedZoneSchema = Yup.object().shape({
  provinceCode: Yup.string().required(),
  cities: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required(),
      name: Yup.string().required(),
    }),
  ),
});

export const createValidationSchema = () =>
  Yup.object().shape({
    selectedShippingMethods: conditionalArrayRequired(
      'shippingMethodsScope',
      ShippingRestrictionScope.SPECIFIC,
      'deliveryMethods.shippingLimiting.validations.methodsRequired',
    ),

    distributionCenterScope: Yup.mixed<ShippingRestrictionScope>()
      .oneOf([ShippingRestrictionScope.ALL, ShippingRestrictionScope.SPECIFIC])
      .required(),

    specificDistributionCenters: conditionalArrayRequired(
      'distributionCenterScope',
      ShippingRestrictionScope.SPECIFIC,
      'deliveryMethods.shippingLimiting.validations.centersRequired',
    ),

    deliveryZonesScope: Yup.mixed<DeliveryZonesScope>()
      .oneOf([
        DeliveryZonesScope.ALL,
        DeliveryZonesScope.GEOGRAPHICAL_ZONE,
        DeliveryZonesScope.DISTANCE_IN_KM,
      ])
      .required(),

    selectedZones: Yup.array()
      .of(selectedZoneSchema)
      .when('deliveryZonesScope', (scope, schema) =>
        scope === DeliveryZonesScope.GEOGRAPHICAL_ZONE
          ? schema.min(
              1,
              'deliveryMethods.shippingLimiting.validations.zonesRequired',
            )
          : schema,
      ),

    deliveryDistanceInKm: Yup.string().when(
      'deliveryZonesScope',
      (scope, schema) =>
        scope === DeliveryZonesScope.DISTANCE_IN_KM
          ? schema.test(
              'distanceRange',
              'validations.distanceRange',
              (value) => {
                if (!value) return false;
                const numValue = Number.parseFloat(value);
                if (Number.isNaN(numValue)) return false;
                return (
                  numValue >= MIN_DISTANCE_KM && numValue <= MAX_DISTANCE_KM
                );
              },
            )
          : schema,
    ),

    restrictedCategories: conditionalArrayRequired(
      'productRestrictionType',
      ProductRestrictionType.CATEGORIES,
      'deliveryMethods.shippingLimiting.validations.categoriesRequired',
      Yup.number(),
    ),

    restrictedProducts: conditionalArrayRequired(
      'productRestrictionType',
      ProductRestrictionType.PRODUCTS,
      'deliveryMethods.shippingLimiting.validations.productsRequired',
    ),

    cartWeightMax: Yup.string().when('weightRestrictionType', (type, schema) =>
      type === WeightRestrictionType.WEIGHT_RANGE
        ? schema
            .test(
              'weightMaxNegativesValues',
              'validations.negativeValues',
              (value) => {
                if (value) {
                  const numValue = Number(value);
                  if (Number.isNaN(numValue) || numValue < 0) {
                    return false;
                  }
                }
                return true;
              },
            )
            .test(
              'weightMax',
              'validations.weightMax',
              (value, { parent: { cartWeightMin } }) => {
                if (cartWeightMin && value) {
                  const min = Number(cartWeightMin);
                  const max = Number(value);
                  return !Number.isNaN(max) && !Number.isNaN(min) && max >= min;
                }
                return true;
              },
            )
        : schema,
    ),

    cartWeightMin: Yup.string()
      .test(
        'weightMinNegativesValues',
        'validations.negativeValues',
        (value) => {
          if (value) {
            const numValue = Number(value);
            if (Number.isNaN(numValue) || numValue < 0) {
              return false;
            }
          }
          return true;
        },
      )
      .when('weightRestrictionType', (type, schema) =>
        type === WeightRestrictionType.WEIGHT_RANGE
          ? schema.test(
              'atLeastOneWeightField',
              'validations.atLeastOneWeightField',
              (value, { parent: { cartWeightMax } }) =>
                Boolean(value) || Boolean(cartWeightMax),
            )
          : schema,
      ),

    cartValueMax: Yup.string().when('valueRestrictionType', (type, schema) =>
      type === ValueRestrictionType.VALUE_RANGE
        ? schema
            .test(
              'valueMaxNegativesValues',
              'validations.negativeValues',
              (value) => {
                if (value) {
                  const numValue = Number(value);
                  if (Number.isNaN(numValue) || numValue < 0) {
                    return false;
                  }
                }
                return true;
              },
            )
            .test(
              'valueMax',
              'validations.valueMax',
              (value, { parent: { cartValueMin } }) => {
                if (cartValueMin && value) {
                  const min = Number(cartValueMin);
                  const max = Number(value);
                  return !Number.isNaN(max) && !Number.isNaN(min) && max >= min;
                }
                return true;
              },
            )
        : schema,
    ),

    cartValueMin: Yup.string()
      .test(
        'valueMinNegativesValues',
        'validations.negativeValues',
        (value) => {
          if (value) {
            const numValue = Number(value);
            if (Number.isNaN(numValue) || numValue < 0) {
              return false;
            }
          }
          return true;
        },
      )
      .when('valueRestrictionType', (type, schema) =>
        type === ValueRestrictionType.VALUE_RANGE
          ? schema.test(
              'atLeastOneValueField',
              'validations.atLeastOneValueField',
              (value, { parent: { cartValueMax } }) =>
                Boolean(value) || Boolean(cartValueMax),
            )
          : schema,
      ),
  });
